/**
 * React Query hook for V4 Dashboard Rollup data (Export functionality)
 */

import { useQuery } from '@tanstack/react-query'
import httpClient from 'src/utilities/http-client'
import { DashboardQueryParams, RollupDataItem } from '../types'

/**
 * Query key factory for rollup data
 */
export let rollupQueryKeys = {
  all: ['v4-dashboard', 'rollup'] as const,
  data: (params: DashboardQueryParams) =>
    [...rollupQueryKeys.all, 'data', params] as const,
}

/**
 * Build rollup URL with only basic parameters (no advanced filtering)
 */
function buildRollupUrl(params: DashboardQueryParams): string {
  let searchParams = new URLSearchParams()

  // Only use basic required parameters for rollup endpoint
  searchParams.append('linkedServiceID', params.linkedServiceID)
  searchParams.append('startDate', params.startDate)
  searchParams.append('endDate', params.endDate)

  return `/api/manage/dashboard/rollup?${searchParams.toString()}`
}

/**
 * Fetch rollup data from the API
 */
async function fetchRollupData(
  params: DashboardQueryParams,
): Promise<RollupDataItem[]> {
  let url = buildRollupUrl(params)
  let response = await httpClient.get<{ data: RollupDataItem[] }>(url)
  return response.data.data
}

/**
 * Hook to fetch rollup data with React Query
 */
export function useRollupData(
  params: DashboardQueryParams,
  enabled: boolean = true,
) {
  let isEnabled =
    enabled &&
    Boolean(params.linkedServiceID && params.startDate && params.endDate)

  return useQuery({
    queryKey: rollupQueryKeys.data(params),
    queryFn: () => fetchRollupData(params),
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}
